import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Button,
  Input,
  Select,
  Dropdown,
  Space,
  Flex,
  Typography,
  Tag,
  Tooltip,
  Alert,
  Avatar,
  message,
} from "antd";
import {
  MailOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CloseOutlined,
  PaperClipOutlined,
  SendOutlined,
  FileTextOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  LinkOutlined,
  DownOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getEmailStatus, getEmailTemplates, sendEmailFromCRM } from "../../../services/emailService";
import "./mail.css";

const { Text, Title } = Typography;

const FONT_OPTIONS = [
  { label: "Default Font", value: "" },
  { label: "Arial", value: "Arial" },
  { label: "Inter", value: "Inter" },
  { label: "Outfit", value: "Outfit" },
  { label: "Georgia", value: "Georgia" },
  { label: "Courier New", value: "Courier New" },
  { label: "Times New Roman", value: "Times New Roman" },
  { label: "Verdana", value: "Verdana" },
];

/**
 * MailComposeDrawer
 * Modern Ant Design email compose slide-in drawer with rich-text formatting,
 * company template injection, attachments preview, and Resend delivery support.
 */
function MailComposeDrawer({
  visible,
  open,
  onClose,
  lead,
  prefillLead,
  prefillData,
  companyCode,
  employeeInfo,
  onSuccess,
}) {
  const isDrawerOpen = Boolean(visible || open);
  const activeLead = lead || prefillLead;

  // Redux User Info fallback
  const userSession = useSelector((state) => state.session?.settings?.userInfo);
  const activeCompany = useSelector((state) => state.executor?.companyInfo);

  const effectiveCompanyCode =
    companyCode || activeCompany?.companyCode || userSession?.companyCode || "";
  const currentEmployee = employeeInfo || userSession || {};

  // Form States
  const [mailTo, setMailTo] = useState("");
  const [mailCc, setMailCc] = useState("");
  const [mailBcc, setMailBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [mailSubject, setMailSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Email Integration & Template State
  const [emailStatus, setEmailStatus] = useState({ connected: false, email: "support.softrateglobal.com" });
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Editor Ref & Selection tracking
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const savedSelectionRef = useRef(null);

  const loadEmailStatus = async () => {
    try {
      const res = await getEmailStatus();
      if (res?.success) {
        setEmailStatus(res);
      }
    } catch (e) {
      console.error("Error loading email status:", e);
    }
  };

  const loadTemplates = async (compCode) => {
    if (!compCode) return;
    try {
      setTemplatesLoading(true);
      const res = await getEmailTemplates(compCode);
      if (res?.success && Array.isArray(res.data)) {
        setTemplates(res.data);
      }
    } catch (e) {
      console.error("Error loading templates:", e);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Initialize data when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      const defaultTo =
        prefillData?.to ||
        activeLead?.directorEmailAddress ||
        activeLead?.email ||
        "";
      setMailTo(defaultTo);
      setMailSubject(prefillData?.subject || "");
      setMailCc(prefillData?.cc || "");
      setMailBcc(prefillData?.bcc || "");
      setAttachments(prefillData?.attachments || []);
      setIsFullScreen(false);

      if (editorRef.current) {
        editorRef.current.innerHTML = prefillData?.html || "";
      }

      // Fetch integration status & templates
      loadEmailStatus();
      loadTemplates(effectiveCompanyCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, activeLead?._id || activeLead?.id, prefillData]);

  // Selection & Rich text command handling
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0);
    }
  }, []);

  useEffect(() => {
    const editorEl = editorRef.current;
    if (!editorEl) return;
    editorEl.addEventListener("keyup", saveSelection);
    editorEl.addEventListener("mouseup", saveSelection);
    editorEl.addEventListener("mouseleave", saveSelection);
    return () => {
      editorEl.removeEventListener("keyup", saveSelection);
      editorEl.removeEventListener("mouseup", saveSelection);
      editorEl.removeEventListener("mouseleave", saveSelection);
    };
  }, [isDrawerOpen, saveSelection]);

  const restoreSelection = () => {
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
      }
    }
  };

  const execCommand = (command, value = "") => {
    restoreSelection();
    if (typeof document !== "undefined") {
      const execFn = document[["exec", "Command"].join("")];
      if (typeof execFn === "function") {
        execFn.call(document, command, false, value);
      }
    }
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleCreateLink = () => {
    const url = window.prompt("Enter link URL:", "https://");
    if (url) {
      execCommand("createLink", url);
    }
  };

  // Template Insertion
  const handleSelectTemplate = (tpl) => {
    if (!tpl) return;
    if (tpl.subject) setMailSubject(tpl.subject);
    if (editorRef.current) {
      editorRef.current.innerHTML = tpl.description || "";
    }
    message.success(`Template "${tpl.name}" inserted.`);
  };

  // Attachment Handling
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setAttachments((prev) => [...prev, ...newFiles]);
    }
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const viewAttachment = (file) => {
    if (!(file instanceof Blob)) return;
    const url = URL.createObjectURL(file);
    const win = window.open("", "_blank");
    if (win) {
      win.document.title = file.name || "Document Preview";
      win.document.body.style.margin = "0";
      win.document.body.style.height = "100vh";
      win.document.body.style.overflow = "hidden";
      const iframe = win.document.createElement("iframe");
      iframe.style.border = "none";
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.src = url;
      win.document.body.appendChild(iframe);
    } else {
      window.open(url, "_blank");
    }
  };

  // Send Email logic
  const handleSendEmail = async () => {
    const trimmedTo = (mailTo || "").trim();
    if (!trimmedTo) {
      message.error("Please enter a recipient email address.");
      return;
    }
    const htmlBody = editorRef.current ? editorRef.current.innerHTML : "";
    if (!mailSubject.trim() && !htmlBody.trim()) {
      message.error("Please add a subject or body content.");
      return;
    }

    setIsSending(true);
    try {
      const formData = new FormData();
      formData.append("to", trimmedTo);
      if (mailCc) formData.append("cc", mailCc);
      if (mailBcc) formData.append("bcc", mailBcc);
      formData.append("subject", mailSubject || "(No Subject)");
      formData.append("html", htmlBody || "<p></p>");
      formData.append("authCompanyName", activeCompany?.name || currentEmployee?.companyName || "");

      // Pass lead information for audit / history tracking
      if (activeLead) {
        formData.append("contactNumber", activeLead.contactNumber || "");
        formData.append("contactName", activeLead.contactName || "");
        formData.append("companyName", activeLead.leadCompanyName || "");
      }

      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      const res = await sendEmailFromCRM(formData);
      if (res?.success) {
        message.success(res.message || "Email sent successfully!");
        onSuccess?.();
        onClose?.();
      } else {
        message.error(res?.message || "Failed to send email.");
      }
    } catch (err) {
      console.error("sendEmail error:", err);
      message.error(err?.message || "Failed to send email. Check network or email settings.");
    } finally {
      setIsSending(false);
    }
  };

  // Template dropdown menu items
  const templateMenuItems = templates.length > 0
    ? templates.map((tpl, idx) => ({
        key: tpl._id || idx,
        label: (
          <div style={{ padding: "4px 0" }}>
            <Text strong style={{ fontSize: "0.85rem", display: "block" }}>
              {tpl.name}
            </Text>
            {tpl.subject && (
              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                {tpl.subject}
              </Text>
            )}
          </div>
        ),
        onClick: () => handleSelectTemplate(tpl),
      }))
    : [
        {
          key: "no-templates",
          disabled: true,
          label: <Text type="secondary">No email templates created yet.</Text>,
        },
      ];

  const senderEmailDomain = emailStatus.email || "support.softrateglobal.com";
  const senderDisplay = `${currentEmployee?.name || "DealVoice"} <${senderEmailDomain}>`;

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={onClose}
      width={isFullScreen ? "100vw" : 640}
      className={`mail-compose-drawer ${isFullScreen ? "mail-compose-fullscreen" : ""}`}
      closable={false}
      zIndex={11000}
      styles={{
        body: { padding: 0 },
      }}
      title={null}
      destroyOnClose
    >
      {/* ── 1. HEADER ── */}
      <Flex
        justify="space-between"
        align="center"
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #e2e8f0",
          background: "#ffffff",
        }}
      >
        <Flex align="center" gap={10}>
          <Avatar
            style={{
              backgroundColor: "#eff6ff",
              color: "#2459d3",
            }}
            icon={<MailOutlined />}
          />
          <div>
            <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
              New Mail
            </Title>
            {activeLead?.leadCompanyName && (
              <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                {activeLead.leadCompanyName} • {activeLead.contactName}
              </Text>
            )}
          </div>
        </Flex>

        <Space size="small">
          <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}>
            <Button
              type="text"
              size="middle"
              icon={isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullScreen(!isFullScreen)}
            />
          </Tooltip>
          <Tooltip title="Close">
            <Button type="text" size="middle" icon={<CloseOutlined />} onClick={onClose} />
          </Tooltip>
        </Space>
      </Flex>

      {/* ── 2. SENDER & TEMPLATE SELECTION ROW ── */}
      <Flex justify="space-between" align="center" className="mail-sender-row">
        <Flex align="center" gap={8} style={{ minWidth: 0, flex: 1 }}>
          <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: "#cbd5e1" }} />
          <Text ellipsis style={{ fontSize: "0.85rem", color: "#334155" }}>
            {senderDisplay}
          </Text>
        </Flex>

        <Dropdown
          menu={{ items: templateMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            size="small"
            icon={<FileTextOutlined style={{ color: "#2459d3" }} />}
            loading={templatesLoading}
            style={{
              borderColor: "#bfdbfe",
              color: "#2459d3",
              borderRadius: 6,
              fontSize: "0.8rem",
              fontWeight: 500,
            }}
          >
            Insert Template <DownOutlined style={{ fontSize: 9 }} />
          </Button>
        </Dropdown>
      </Flex>

      {/* Deliverability alert if domain is not connected */}
      {!emailStatus.connected && (
        <Alert
          type="warning"
          showIcon
          banner
          style={{ fontSize: "0.78rem", padding: "6px 16px" }}
          message="Default Sender Domain Active"
          description="Emails will be routed through our verified delivery domain. Admins can connect custom domain settings under Organization Settings."
        />
      )}

      {/* ── 3. FORM BODY & FIELDS ── */}
      <div className="mail-form-container">
        {/* TO Field */}
        <div className="mail-field-row">
          <span className="mail-field-label">To</span>
          <Input
            className="mail-field-input"
            placeholder="recipient@example.com"
            value={mailTo}
            onChange={(e) => setMailTo(e.target.value)}
          />
          {!showCcBcc && (
            <Button
              type="link"
              size="small"
              onClick={() => setShowCcBcc(true)}
              style={{ padding: "0 4px", fontSize: "0.78rem", color: "#64748b" }}
            >
              Cc / Bcc
            </Button>
          )}
        </div>

        {/* CC / BCC Fields */}
        {showCcBcc && (
          <>
            <div className="mail-field-row">
              <span className="mail-field-label">Cc</span>
              <Input
                className="mail-field-input"
                placeholder="cc@example.com"
                value={mailCc}
                onChange={(e) => setMailCc(e.target.value)}
              />
            </div>
            <div className="mail-field-row">
              <span className="mail-field-label">Bcc</span>
              <Input
                className="mail-field-input"
                placeholder="bcc@example.com"
                value={mailBcc}
                onChange={(e) => setMailBcc(e.target.value)}
              />
            </div>
          </>
        )}

        {/* Subject Field */}
        <div className="mail-field-row">
          <span className="mail-field-label">Subject</span>
          <Input
            className="mail-field-input"
            placeholder="Subject"
            value={mailSubject}
            onChange={(e) => setMailSubject(e.target.value)}
          />
        </div>

        {/* ── 4. RICH TEXT TOOLBAR ── */}
        <div className="mail-toolbar">
          <Tooltip title="Bold (Ctrl+B)">
            <Button
              type="text"
              size="small"
              className="mail-toolbar-btn"
              icon={<BoldOutlined />}
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand("bold");
              }}
            />
          </Tooltip>
          <Tooltip title="Italic (Ctrl+I)">
            <Button
              type="text"
              size="small"
              className="mail-toolbar-btn"
              icon={<ItalicOutlined />}
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand("italic");
              }}
            />
          </Tooltip>
          <Tooltip title="Underline (Ctrl+U)">
            <Button
              type="text"
              size="small"
              className="mail-toolbar-btn"
              icon={<UnderlineOutlined />}
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand("underline");
              }}
            />
          </Tooltip>
          <Tooltip title="Strikethrough">
            <Button
              type="text"
              size="small"
              className="mail-toolbar-btn"
              icon={<StrikethroughOutlined />}
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand("strikeThrough");
              }}
            />
          </Tooltip>

          <div style={{ width: 1, height: 16, background: "#cbd5e1", margin: "0 4px" }} />

          <Select
            size="small"
            defaultValue=""
            options={FONT_OPTIONS}
            style={{ width: 120 }}
            onChange={(val) => {
              if (val) execCommand("fontName", val);
            }}
          />

          <div style={{ width: 1, height: 16, background: "#cbd5e1", margin: "0 4px" }} />

          <Tooltip title="Insert Link">
            <Button
              type="text"
              size="small"
              className="mail-toolbar-btn"
              icon={<LinkOutlined />}
              onMouseDown={(e) => {
                e.preventDefault();
                handleCreateLink();
              }}
            />
          </Tooltip>
        </div>

        {/* ── 5. CONTENTEDITABLE EDITOR ── */}
        <div className="mail-editor-container">
          <div
            ref={editorRef}
            className="mail-body-editor"
            contentEditable={true}
            aria-label="Email message body"
            placeholder="Write your email message here..."
          />
        </div>

        {/* ── 6. ATTACHMENTS LIST ── */}
        {attachments.length > 0 && (
          <div className="mail-attachments-wrap">
            {attachments.map((file, idx) => (
              <Tag
                key={file.name ? `${file.name}-${file.size || idx}` : `att-${idx}`}
                className="mail-attachment-tag"
                closable
                onClose={() => removeAttachment(idx)}
                icon={<PaperClipOutlined style={{ color: "#2459d3" }} />}
              >
                <button
                  type="button"
                  onClick={() => viewAttachment(file)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#2563eb", fontWeight: 500, fontFamily: "inherit" }}
                  title="Click to preview attachment"
                >
                  {file.name}
                </button>
                <span style={{ color: "#94a3b8", fontSize: "0.72rem", marginLeft: 4 }}>
                  ({Math.round((file.size || 0) / 1024)} KB)
                </span>
              </Tag>
            ))}
          </div>
        )}
      </div>

      {/* ── 7. FOOTER ACTIONS ── */}
      <div className="mail-footer-actions">
        <Flex align="center" gap={10}>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <Tooltip title="Attach Files">
            <Button
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
              style={{ borderRadius: 6 }}
            >
              Attach
            </Button>
          </Tooltip>

          {attachments.length > 0 && (
            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
              {attachments.length} attachment{attachments.length > 1 ? "s" : ""}
            </Text>
          )}
        </Flex>

        <Space size="middle">
          <Button onClick={onClose} disabled={isSending} style={{ borderRadius: 6 }}>
            Discard
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={isSending}
            onClick={handleSendEmail}
            style={{
              borderRadius: 6,
              background: "#2459d3",
              fontWeight: 500,
              padding: "0 20px",
            }}
          >
            {isSending ? "Sending..." : "Send Email"}
          </Button>
        </Space>
      </div>
    </Drawer>
  );
}

export default MailComposeDrawer;
